import React, { useState, useEffect, useRef } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Shield, FileText, MapPin, Book, Car, AlertTriangle, Camera, Share2, Upload, Search, ChevronRight, ChevronLeft, X, Menu, Home, User, Users, Info, Phone, Scale, Clock, DollarSign, CheckCircle, Navigation, Star, ExternalLink, Bell, Calendar, Volume2, VolumeX, Globe, WifiOff, Plus, Trash2, Play, Pause, Radio, CloudRain, Sun, Eye, Timer, Receipt, RefreshCw, Settings, Gavel, AlertCircle, Calculator, MessageCircle, HelpCircle, Filter, Zap, GraduationCap, BookOpen, Building2, Send, Briefcase, Image, Award, Target, ChevronDown, ChevronUp, Clipboard, UserCheck, BadgeCheck, Wrench, LogIn, LogOut, UserCog, Lock, Box, Tag, BarChart3, Truck, Store, CreditCard, Smartphone, Mail, Loader2, Heart } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LoginPage, AdminDashboard, AdminSubmissionsPage, AdminUsersPage, AdminAllListingsPage, AdminSettingsPage, AdminContactSubmissions as AdminContactSubmissionsPage } from './components/Admin';
import { AdminSubmissionsPageEnhanced, AdminServiceProvidersPage, AdminSubscriptionsPage, AdminPaymentsPage, AdminEmailTemplatesPage, AdminPaymentRequestsPage, AdminProviderReportsPage, AdminProviderReviewsPage } from './components/AdminServiceProviders';
import { AdminAnalyticsPage, AdminSettingsPageEnhanced } from './components/AdminAnalytics';
import { AdminBannerAdsPage } from './components/AdminBannerAds';
import { AdminHeroCarouselPage } from './components/AdminHeroCarousel';
import { HeroCarousel } from './components/HeroCarousel';
import { heroCarousel } from './lib/supabase';
import { AdvertiserPortal } from './components/AdvertiserPortal';
import VendorPortalPage from './components/VendorPortal';
import { ServiceProvidersPage } from './components/ServiceProvidersPage';
import { GetListedPageEnhanced } from './components/GetListedPageEnhanced';
import { SEO, WebsiteSchema, OrganizationSchema, PAGE_SEO } from './components/SEO';
import { TopBanner, BottomBanner, InlineBanner1, InlineBanner2, InlineBanner3 } from './components/BannerAd';
import { initGA4, trackPageView } from './lib/analytics';
import { initAdSense, isAdSenseConfigured } from './lib/adsense';
import { appSettings } from './lib/supabase';
import { DataCorrectionReport } from './components/DataCorrectionReport';
import { AdminDataCorrectionsPage } from './components/AdminDataCorrections';
import { AdminNotificationsPage, NotificationBell } from './components/AdminNotifications';
import { UserDashboard } from './components/UserDashboard';
import GlobalSearch from './components/GlobalSearch';
import {
  EmergencyAlertBanner,
  EducationHero,
  HeroStats,
  HeroSupport,
  ForEveryFamilySection,
  InteractiveToolsSection,
  SupportSafetySection,
  HowSchoolWorksPage,
  EnrollmentPage,
  ExamsHubPage,
  PEPExamPage,
  CSECExamPage,
  CAPEExamPage,
  NVQJExamPage,
  SchoolDirectoryPage,
  SchoolProfilePage,
  RankingsHubPage,
  AcademicRankingsPage,
  FundingPage,
  HelpPage
} from './components/EducationPages';
// Moved out of EducationPages: the board grew a submission form, a
// poster's own-listings view and a moderation status, which is more than a
// section of a shared file should carry.
import { EducationJobsPage } from './components/EducationJobsPage';
import { AdminEducationJobsPage } from './components/AdminEducationJobs';
import {
  SpecialEducationSupportPage,
  ChildProtectionPage,
  CityGuildsExamPage,
  SATExamPage
} from './components/EducationFeaturesNew';
import { CareerPathVisualiser } from './components/CareerPathVisualiserNew';
import { LifePathSimulator } from './components/LifePathSimulatorNew';
import { HowThisGuideWorksPage } from './components/HowThisGuideWorksPage';
import { CompareSchoolsPage } from './components/CompareSchoolsPage';
import { SchoolsByParishPage } from './components/SchoolsByParishPage';
import { ExamTrackerPage as ExamResourcesPage } from './components/ExamTrackerPage';
import {
  ExtracurricularRankingsPage,
  EnvironmentRankingsPage,
  HotSchoolsPage,
  ShareSentimentPage,
  SuggestCorrectionPage,
  SubmitAchievementPage
} from './components/RankingsPages';
import { StudentSideHustlesPage } from './components/StudentSideHustlesPage';
import { SummerEmploymentPage } from './components/SummerEmploymentPage';
import { OpportunityMatcherPage } from './components/OpportunityMatcherPage';
import { ExtracurricularsPage } from './components/ExtracurricularsPage';
import {
  AdminSchoolsPage,
  AdminExamResultsPage,
  AdminDisruptionsPage,
  AdminSuggestionsPage
} from './components/AdminEducation';
import { AdminStudentEngagementPage } from './components/AdminStudentEngagement';
import { AdminSchoolReviewsPage } from './components/AdminSchoolReviews';
import { QuickAnswerBotPage as EducationAskBot } from './components/EducationAskBot';
import { TertiaryEducationHub } from './components/TertiaryEducationHub';
import { PrimaryEducationHub } from './components/PrimaryEducationHub';
import { SecondChanceEducationHub } from './components/SecondChanceEducationHub';
import { SecondaryEducationHub } from './components/SecondaryEducationHub';
import { AllSchoolsPage } from './components/AllSchoolsPage';
import { TertiaryPollsPage, PrimaryPollsPage } from './components/EducationPolls';
import { AboutEditorialPage, EducationUpdatesPage, TermsPage } from './components/TrustAndUpdatesPages';

// ==================== CUSTOM HOOK ====================
const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch { return initialValue; }
  });
  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      localStorage.setItem(key, JSON.stringify(valueToStore));
      return { success: true };
    } catch (e) {
      console.error('localStorage error:', e);
      return { success: false, error: e.name === 'QuotaExceededError' ? 'Storage quota exceeded. Try removing some photos or old reports.' : 'Failed to save. Please try again.' };
    }
  };
  return [storedValue, setValue];
};

// ==================== BACK BUTTON COMPONENT ====================
const BackButton = ({ setPage, goBack, label = "Back" }) => (
  <button onClick={() => goBack ? goBack() : setPage('home')}
    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium">
    <ChevronLeft className="w-5 h-5" />{label}
  </button>
);

// ==================== AUTH GATE COMPONENT ====================
// Protects sensitive pages that require authentication
const AuthGate = ({ children, setPage, goBack, pageName, pageIcon: PageIcon }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 to-yellow-50 p-4 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 to-yellow-50 p-4">
        <div className="max-w-lg mx-auto pt-8">
          <BackButton setPage={setPage} goBack={goBack} />
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-brand-100 to-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-brand-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Sign In Required</h2>
            <p className="text-gray-600 mb-6">
              {pageName} contains your personal data. Please sign in or create an account to access this feature securely.
            </p>
            <div className="bg-brand-50 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-center gap-3 text-brand-700">
                {PageIcon && <PageIcon className="w-6 h-6" />}
                <span className="font-medium">{pageName}</span>
              </div>
              <p className="text-sm text-brand-600 mt-2">Your data is stored securely and only visible to you</p>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => setPage('login')}
                className="w-full py-3 bg-gradient-to-r from-brand-600 to-brand-700 text-white font-bold rounded-xl hover:from-brand-700 hover:to-brand-800 transition-all flex items-center justify-center gap-2"
              >
                <LogIn className="w-5 h-5" />
                Sign In / Register
              </button>
              <button
                onClick={() => setPage('home')}
                className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all"
              >
                Back to Home
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-6">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

// ==================== TRANSLATIONS ====================
const T = {
  en: {
    // App
    appName: "Jamaica Schools Guide",
    tagline: "Find Schools, Compare Outcomes, Plan Careers",

    // Navigation
    home: "Home",
    policeStopGuide: "How School Works",
    offencesFines: "Exams & Results",
    myDocuments: "My Documents",
    resources: "Resources",
    demeritPoints: "Rankings",
    ticketTracker: "My Progress",
    accidentReport: "Report Issue",
    traffic: "News",
    settings: "Settings",

    // Hero
    stoppedByPolice: "Find the Right School",
    lookUpOffence: "Compare Schools",
    knowYourRights: "Exam Resources",
    onEveryRoad: "& Career Planning",
    heroDesc: "Your complete guide to Jamaica's education system — schools, exams, rankings & scholarships.",

    // Common
    disclaimer: "This app provides general information for educational guidance.",
    yourRights: "Your Options",
    tips: "Tips",
    courtRemedy: "Next Steps",
    fineRange: "Fee Range",
    save: "Save",
    cancel: "Cancel",
    next: "Next",
    previous: "Previous",
    learnMore: "Learn more",
    search: "Search",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    close: "Close",
    back: "Back",
    submit: "Submit",
    delete: "Delete",
    edit: "Edit",
    view: "View",

    // Homepage sections
    quickReference: "Quick Reference",
    yourToolsServices: "Education Tools",
    trafficLocations: "Explore Schools",
    serviceProviders: "Education Services",

    // Quick Reference items
    askBot: "Ask Bot",
    fineCalculator: "Fee Calculator",
    roadCode: "School Guide",
    policeStop: "Enrollment",
    feesSchedule: "Fees Schedule",
    howToGuides: "How-To Guides",
    learnerGuide: "Student Guide",

    // Service Providers
    findServiceProviders: "Find Education Services",
    serviceProvidersDesc: "Tutors, Exam Prep, Uniforms, School Supplies & more",
    getYourBusinessListed: "Get Your Service Listed",
    getListedDesc: "Join our directory of education service providers",

    // Education Banner
    emvrcTitle: "Jamaica Schools Guide",
    emvrcDesc: "Find schools, compare rankings, understand exams, and plan your educational journey in Jamaica.",

    // Disclaimer
    importantDisclaimer: "Important Disclaimer",

    // Offline
    youreOffline: "You're offline - App still works!",

    // Footer
    generalInfoOnly: "General information only, not legal advice.",

    // Police Stop
    whatToDo: "What to do",
    doNot: "Don't",
    remember: "Remember",

    // Offences
    searchOffences: "Search offences...",
    allCategories: "All Categories",
    fixedPenalty: "Fixed Penalty",
    courtOnly: "Court Only",
    points: "Points",

    // Documents
    addDocument: "Add Document",
    noDocuments: "No documents yet",

    // Forms
    required: "Required",
    optional: "Optional",
    selectParish: "Select Parish",
    enterDetails: "Enter details",

    // Ticket Tracker
    addTicket: "Add Ticket",
    noTickets: "No tickets tracked",
    paid: "Paid",
    unpaid: "Unpaid",

    // Accident Report
    startReport: "Start Report",

    // Settings
    language: "Language",
    english: "English",
    patois: "Patois"
  },
  patois: {
    // App
    appName: "Jamaica Schools Guide",
    tagline: "Find Schools, Compare Results, Plan Career",

    // Navigation
    home: "Home",
    policeStopGuide: "How School Work",
    offencesFines: "Exams & Results",
    myDocuments: "Mi Documents",
    resources: "Resources",
    demeritPoints: "Rankings",
    ticketTracker: "Mi Progress",
    accidentReport: "Report Issue",
    traffic: "News",
    settings: "Settings",

    // Hero
    stoppedByPolice: "Find di Right School",
    lookUpOffence: "Compare Schools",
    knowYourRights: "Exam Resources",
    onEveryRoad: "& Career Planning",
    heroDesc: "Yuh complete guide to Jamaica education — schools, exams, rankings & scholarships.",

    // Common
    disclaimer: "Dis app give general info fi education guidance.",
    yourRights: "Yuh Options",
    tips: "Tips",
    courtRemedy: "Next Steps",
    fineRange: "Fee Range",
    save: "Save",
    cancel: "Cancel",
    next: "Next",
    previous: "Back",
    learnMore: "Learn more",
    search: "Search",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    close: "Close",
    back: "Back",
    submit: "Submit",
    delete: "Delete",
    edit: "Edit",
    view: "View",

    // Homepage sections
    quickReference: "Quick Reference",
    yourToolsServices: "Education Tools",
    trafficLocations: "Explore Schools",
    serviceProviders: "Education Services",

    // Quick Reference items
    askBot: "Ask Bot",
    fineCalculator: "Fee Calculator",
    roadCode: "School Guide",
    policeStop: "Enrollment",
    feesSchedule: "Fees Schedule",
    howToGuides: "How-To Guides",
    learnerGuide: "Student Guide",

    // Service Providers
    findServiceProviders: "Find Education Services",
    serviceProvidersDesc: "Tutors, Exam Prep, Uniforms, School Supplies & more",
    getYourBusinessListed: "Get Yuh Service Listed",
    getListedDesc: "Join wi directory fi education service providers",

    // Education Banner
    emvrcTitle: "Jamaica Schools Guide",
    emvrcDesc: "Find schools, compare rankings, understand exams, and plan yuh educational journey inna Jamaica.",

    // Disclaimer
    importantDisclaimer: "Warning",

    // Offline
    youreOffline: "Yuh offline - App still work!",

    // Footer
    generalInfoOnly: "Dis nuh legal advice.",

    // Police Stop
    whatToDo: "Wha fi do",
    doNot: "Nuh do dis",
    remember: "Remember",

    // Offences
    searchOffences: "Search offences...",
    allCategories: "All Categories",
    fixedPenalty: "Fixed Penalty",
    courtOnly: "Court Only",
    points: "Points",

    // Documents
    addDocument: "Add Document",
    noDocuments: "No documents yet",

    // Forms
    required: "Required",
    optional: "Optional",
    selectParish: "Select Parish",
    enterDetails: "Enter details",

    // Ticket Tracker
    addTicket: "Add Ticket",
    noTickets: "No tickets tracked",
    paid: "Paid",
    unpaid: "Unpaid",

    // Accident Report
    startReport: "Start Report",

    // Settings
    language: "Language",
    english: "English",
    patois: "Patois"
  }
};

// ==================== BACK TO TOP BUTTON ====================
const BackToTop = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!visible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-6 right-6 z-50 p-3 bg-slate-800 hover:bg-slate-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110"
      aria-label="Back to top"
    >
      <ChevronUp className="w-6 h-6" />
    </button>
  );
};

// ==================== HEADER ====================
const Header = ({ page, setPage, menuOpen, setMenuOpen, lang, setLang, setSelectedVendor }) => {
  const t = T[lang];
  const { isAuthenticated, isAdmin, profile, profileLoading, signOut, deleteAccount, linkedProviders, hasLinkedBusiness } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Close user menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (userMenuOpen) {
        setUserMenuOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [userMenuOpen]);

  // Top-level nav stays to the 4 "doors" plus genuinely cross-cutting resources.
  // Level-specific tools (PEP, CSEC guides, Career Explorer, HEART, etc.) live
  // inside their door's hub page instead of being duplicated here — see
  // docs/VISUAL_REFRESH.md for the full page-to-door mapping.
  // `short` is what the persistent desktop nav bar renders; `label` stays the
  // full phrase for the mobile sheet and for screen readers, where there is
  // room for it and the extra specificity helps.
  const navItems = [
    { id: 'home', icon: Home, label: t.home, short: 'Home' },
    // Education Gateways - Four main paths ("doors")
    { id: 'primary', icon: BookOpen, label: 'Prep & Primary', short: 'Prep & Primary', door: 'primary' },
    { id: 'schools', icon: Building2, label: 'High Schools', short: 'High Schools', door: 'secondary' },
    { id: 'tertiary', icon: GraduationCap, label: 'Universities & Colleges', short: 'Universities', door: 'tertiary' },
    { id: 'second-chance', icon: RefreshCw, label: 'Second Chance', short: 'Second Chance', door: 'chance' },
    // Cross-cutting resources - relevant regardless of level
    { id: 'rankings', icon: Award, label: 'Rankings', short: 'Rankings' },
    { id: 'exams', icon: FileText, label: 'Exams', short: 'Exams' },
    { id: 'funding', icon: DollarSign, label: 'Scholarships', short: 'Scholarships' },
    { id: 'services', icon: Store, label: 'Services & Tutoring', short: 'Services' },
    { id: 'help', icon: HelpCircle, label: 'Help & FAQs', short: 'Help' }
  ];

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    setPage('home');
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.')) {
      if (window.confirm('This is your final warning. Click OK to permanently delete your account and all associated data.')) {
        await deleteAccount();
        setUserMenuOpen(false);
        setPage('home');
        alert('Your account has been deleted. Thank you for using Jamaica Schools Guide.');
      }
    }
  };

  // Restyle: the header was a flat `bg-slate-800` bar whose ONLY navigation
  // was a hamburger — on desktop too, so a 27" screen showed the same hidden
  // menu as a phone. It is now two tiers: a brand-toned identity bar, and a
  // persistent horizontal nav that is always visible from `lg` up. Below `lg`
  // the nav collapses into a full-width sheet with 56px rows.
  const navLinkBase =
    'group relative flex items-center gap-2 rounded-xl font-semibold transition-all duration-150 ease-out';

  return (
    <header role="banner" className="sticky top-0 z-50 shadow-lg">
      {/* ---------- Tier 1: identity + account ---------- */}
      <div className="bg-gradient-to-r from-brand-900 via-brand-800 to-brand-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between gap-3 h-16 md:h-[72px]">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); setPage('home'); }}
              className="flex items-center gap-3 min-w-0 group"
              aria-label="Jamaica Schools Guide - Go to home page"
            >
              <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 md:w-11 md:h-11 rounded-xl bg-white/10 ring-1 ring-white/20 backdrop-blur transition-all duration-200 group-hover:bg-white/20 group-hover:scale-105">
                <GraduationCap className="w-6 h-6 md:w-7 md:h-7 text-white" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                {/* Wraps to two tight lines on a narrow phone rather than
                    truncating to "Jamaica School…" — the wordmark is the one
                    string on the page that must never be clipped. */}
                <span className="block text-[13px] sm:text-[15px] md:text-lg font-extrabold leading-[1.15] max-w-[9rem] sm:max-w-none">
                  Jamaica Schools Guide
                </span>
                <span className="hidden sm:block text-xs text-brand-200 leading-tight mt-0.5">
                  Find Schools · Compare Outcomes · Plan Careers
                </span>
              </span>
            </a>

            <div className="flex items-center gap-2" role="navigation" aria-label="User actions">
              <GlobalSearch
                setPage={setPage}
                howToGuides={[]}
                serviceProviders={{ tutors: [], test_prep: [], supplies: [], uniforms: [] }}
                lang={lang}
              />

              <div className="relative">
                {isAuthenticated ? (
                  <>
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center gap-2 px-3 min-h-[44px] rounded-xl bg-white/10 ring-1 ring-white/15 hover:bg-white/20 transition-all duration-150 active:scale-[0.97]"
                      aria-label="User menu"
                      aria-expanded={userMenuOpen}
                      aria-haspopup="true"
                    >
                      <User className="w-5 h-5" aria-hidden="true" />
                      <span className="hidden md:inline text-sm font-semibold">
                        {profile?.full_name?.split(' ')[0] || 'Account'}
                      </span>
                    </button>
                    {userMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 bg-white rounded-2xl shadow-xl ring-1 ring-hairline py-2 min-w-[230px] text-ink z-50" role="menu" aria-label="User menu options">
                        <div className="px-4 py-2 border-b border-hairline">
                          <p className="font-bold truncate">{profile?.full_name || profile?.email?.split('@')[0] || 'Account'}</p>
                          <p className="text-xs text-ink-subtle truncate">{profile?.email || 'Loading...'}</p>
                        </div>
                        <button onClick={() => { setPage('dashboard'); setUserMenuOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-canvas-sunken flex items-center gap-2 min-h-[44px] font-medium" role="menuitem">
                          <User className="w-4 h-4 text-ink-subtle" aria-hidden="true" />My Dashboard
                        </button>
                        {(profile?.user_type === 'service_provider' || isAdmin) && (
                          <button onClick={() => { setPage('my-analytics'); setUserMenuOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-canvas-sunken flex items-center gap-2 min-h-[44px] font-medium" role="menuitem">
                            <BarChart3 className="w-4 h-4 text-ink-subtle" aria-hidden="true" />My Analytics
                          </button>
                        )}
                        {isAdmin && (
                          <button onClick={() => { setPage('admin'); setUserMenuOpen(false); }} className="w-full text-left px-4 py-2 hover:bg-canvas-sunken flex items-center gap-2 min-h-[44px] font-medium" role="menuitem">
                            <UserCog className="w-4 h-4 text-ink-subtle" aria-hidden="true" />Admin Dashboard
                          </button>
                        )}
                        <button onClick={handleSignOut} className="w-full text-left px-4 py-2 hover:bg-canvas-sunken flex items-center gap-2 min-h-[44px] font-medium text-ink-muted border-t border-hairline mt-1 pt-2" role="menuitem">
                          <LogOut className="w-4 h-4" aria-hidden="true" />Sign Out
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <button onClick={() => setPage('login')} className="flex items-center gap-2 px-3 min-h-[44px] rounded-xl bg-white/10 ring-1 ring-white/15 hover:bg-white/20 transition-all duration-150 active:scale-[0.97]" aria-label="Login to your account">
                    <LogIn className="w-5 h-5" aria-hidden="true" />
                    <span className="hidden md:inline text-sm font-semibold">Login</span>
                  </button>
                )}
              </div>

              {/* Mobile/tablet only — from `lg` the nav below is always on screen. */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 ring-1 ring-white/15 hover:bg-white/20 transition-all duration-150 active:scale-[0.97]"
                aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={menuOpen}
                aria-controls="main-navigation"
              >
                {menuOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Tier 2: persistent desktop nav ----------
          Ten icon+label buttons at `whitespace-nowrap` need ~1267px, so at the
          lg breakpoint (992px of inner width) the row used to push the whole
          document to 1283px wide and scroll the PAGE sideways. Three things
          keep it contained now: the strip itself scrolls instead of the page
          (`overflow-x-auto`, so nothing is ever unreachable), the icons only
          appear from `xl` where there is room for them, and the padding tightens
          below `xl`. Measured fit: 1227px at xl, and a self-contained scroll
          below that. */}
      <nav className="hidden lg:block bg-brand-800 border-t border-white/10" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <ul className="flex items-center gap-1 py-1.5 w-max min-w-full">
            {navItems.map((item) => {
              const active = page === item.id;
              return (
                <li key={item.id} className="flex-shrink-0">
                  {item.external ? (
                    <a
                      href={item.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${navLinkBase} px-2.5 py-2.5 text-[13px] text-brand-100 hover:text-white hover:bg-white/10`}
                    >
                      <item.icon className="w-4 h-4 hidden xl:block" aria-hidden="true" />
                      {item.short || item.label}
                      <ExternalLink className="w-3 h-3 opacity-60" aria-hidden="true" />
                      <span className="sr-only">(opens in new tab)</span>
                    </a>
                  ) : (
                    <button
                      onClick={() => setPage(item.id)}
                      className={`${navLinkBase} px-2.5 py-2.5 text-[13px] ${
                        active
                          ? 'bg-white text-brand-900 shadow-sm'
                          : 'text-brand-100 hover:text-white hover:bg-white/10'
                      }`}
                      aria-current={active ? 'page' : undefined}
                    >
                      <item.icon className="w-4 h-4 hidden xl:block" aria-hidden="true" />
                      <span className="whitespace-nowrap">{item.short || item.label}</span>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* ---------- Mobile nav sheet ---------- */}
      {menuOpen && (
        <nav
          id="main-navigation"
          className="lg:hidden bg-brand-800 border-t border-white/10 px-3 py-3 max-h-[70vh] overflow-y-auto"
          aria-label="Main navigation"
        >
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {navItems.map((item) => {
              const active = page === item.id;
              return (
                <li key={item.id}>
                  {item.external ? (
                    <a
                      href={item.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-3 px-4 min-h-[56px] rounded-xl text-[15px] font-semibold text-brand-100 hover:bg-white/10 active:scale-[0.99] transition-all"
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                      <span className="truncate">{item.label}</span>
                      <ExternalLink className="w-3.5 h-3.5 opacity-60" aria-hidden="true" />
                      <span className="sr-only">(opens in new tab)</span>
                    </a>
                  ) : (
                    <button
                      onClick={() => { setPage(item.id); setMenuOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 min-h-[56px] rounded-xl text-[15px] font-semibold transition-all active:scale-[0.99] ${
                        active ? 'bg-white text-brand-900 shadow-sm' : 'text-brand-100 hover:bg-white/10'
                      }`}
                      aria-current={active ? 'page' : undefined}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                      <span className="truncate text-left">{item.label}</span>
                      <ChevronRight className={`w-4 h-4 ml-auto flex-shrink-0 ${active ? 'text-brand-700' : 'text-brand-300'}`} aria-hidden="true" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </header>
  );
};

// ==================== FOOTER NAV ====================
// Collapsible below `md`, always open from `md` up.
//
// The footer carries 12 links across two columns. Stacked on a phone that is
// most of a screen of chrome under every page, and it sits directly after the
// "show more" controls on the long hub pages — so the reader who just expanded
// a list to reach the end of the content hits another wall of links.
//
// The heading is rendered twice rather than as one dual-purpose control: a
// plain <h2> from `md`, and a disclosure <button> below it. A single button
// with `md:pointer-events-none` would keep reporting aria-expanded="false" on
// desktop while its list was plainly visible, which is worse than duplicating
// six words of markup.
const FooterNav = ({ title, links, navigateTo }) => {
  const [open, setOpen] = useState(false);
  const listId = `footer-${title.replace(/\W+/g, '-').toLowerCase()}`;
  const headingClass = 'text-sm font-bold uppercase tracking-[0.12em] text-brand-300';

  return (
    <nav aria-label={title} className="border-b border-white/10 md:border-0 last:border-0">
      <h2 className={`hidden md:block mb-4 ${headingClass}`}>{title}</h2>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={listId}
        className={`md:hidden w-full flex items-center justify-between min-h-[52px] ${headingClass}`}
      >
        {title}
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      <ul id={listId} className={`${open ? 'block' : 'hidden'} md:block space-y-1 pb-3 md:pb-0`}>
        {links.map((l) => (
          <li key={l.id}>
            <button
              onClick={() => navigateTo(l.id)}
              className="inline-flex items-center min-h-[44px] md:min-h-[40px] text-[15px] text-brand-100 hover:text-white hover:underline underline-offset-4 transition-colors"
            >
              {l.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// ==================== HOME HERO ====================
// Renders the admin-managed carousel when slides are published, and the
// standing EducationHero otherwise — never both. See the mount site for why
// stacking them is unsafe.
const HomeHero = ({ setPage, lang }) => {
  const [hasSlides, setHasSlides] = useState(null);

  useEffect(() => {
    let live = true;
    heroCarousel
      .getActiveSlides()
      .then((res) => live && setHasSlides(Boolean(res?.data?.length)))
      .catch(() => live && setHasSlides(false));
    return () => {
      live = false;
    };
  }, []);

  // Until the answer is known, show the standing hero: it is the safe default,
  // it needs no network round-trip, and it avoids a blank gap above the fold.
  if (!hasSlides) return <EducationHero setPage={setPage} />;
  // Slide as the banner, with the headline figures and the five quick actions
  // carried underneath it: the slide is editorial and carries its own CTA, but
  // the stats are the site's proof of substance and the tiles are its primary
  // navigation, so neither should disappear when a banner goes up.
  return (
    <>
      <HeroCarousel setPage={setPage} lang={lang} aside={<HeroStats onImage compact />} />
      <HeroSupport setPage={setPage} />
    </>
  );
};

// ==================== HOME PAGE ====================
const HomePage = ({ setPage, goBack, lang }) => {
  const t = T[lang] || T.en;
  const { isAdmin } = useAuth();

  // Education gateway cards with their resources organized by level
  const educationGateways = [
    {
      id: 'primary',
      title: 'Prep & Primary Schools',
      subtitle: 'Ages 3-11 | Early Childhood to Grade 6',
      description: 'Basic, infant and primary schools — plus PEP preparation.',
      gradient: 'from-door-primary to-door-primary-dark',
      icon: BookOpen,
      mainPage: 'primary',
      resources: [
        { title: 'PEP Exam Guide', page: 'exams-pep', icon: FileText },
        { title: 'Enrollment Info', page: 'enrollment', icon: Users },
        { title: 'Special Education', page: 'special-education', icon: Heart }
      ]
    },
    {
      id: 'secondary',
      title: 'High Schools',
      subtitle: 'Ages 12-18 | Grades 7-13',
      description: 'Rankings, CSEC & CAPE, and side-by-side school comparisons.',
      gradient: 'from-door-secondary to-door-secondary-dark',
      icon: Building2,
      mainPage: 'schools',
      resources: [
        { title: 'School Rankings', page: 'rankings', icon: Award },
        { title: 'CSEC Guide', page: 'exams-csec', icon: FileText },
        { title: 'CAPE Guide', page: 'exams-cape', icon: GraduationCap },
        { title: 'Compare Schools', page: 'compare-schools', icon: Scale }
      ]
    },
    {
      id: 'tertiary',
      title: 'Universities & Colleges',
      subtitle: 'Post-Secondary Education',
      description: 'Universities, colleges and vocational training.',
      gradient: 'from-door-tertiary to-door-tertiary-dark',
      icon: GraduationCap,
      mainPage: 'tertiary',
      resources: [
        { title: 'Career Explorer', page: 'career-path', icon: Briefcase },
        { title: 'Scholarships', page: 'funding', icon: DollarSign },
        { title: 'Education Jobs', page: 'education-jobs', icon: Briefcase }
      ]
    },
    {
      id: 'second-chance',
      title: 'Second Chance Education',
      subtitle: 'For Those Returning to Learning',
      description: "Never too late — free training and evening classes.",
      gradient: 'from-door-chance to-door-chance-dark',
      icon: RefreshCw,
      mainPage: 'second-chance',
      resources: [
        { title: 'HEART Training', page: 'second-chance', icon: Award },
        { title: 'Evening CSEC', page: 'exams-csec', icon: Clock },
        { title: 'Mature Entry', page: 'tertiary', icon: GraduationCap }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      {/* Emergency Alert Banner */}
      <EmergencyAlertBanner />

      {/* Admin-managed hero slider, REPLACING the standing hero rather than
          stacking above it.
          HeroCarousel was imported but never rendered anywhere — slides could
          be created and image-uploaded in Admin > Hero Carousel and would never
          appear on the site.
          It has to replace rather than stack: database/SETUP-ALL.sql seeds
          hero_slides with an is_active default slide whose heading, subtext and
          two CTAs restate the standing hero almost verbatim, so any deployment
          set up from that script would have shown two heroes the moment this
          was mounted. The carousel is itself a hero — its own empty state
          renders one — so exactly one of the two belongs on the page. */}
      <HomeHero setPage={setPage} lang={lang} />

      {/* Top Banner Ad */}
      <TopBanner page="home" />

      {/* Four Education Gateways ("doors") - Main Navigation */}
      <section>
        <div className="mb-5 flex items-start gap-4">
          <span className="hidden sm:flex flex-shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-brand-50 ring-1 ring-brand-100" aria-hidden="true">
            <Target className="w-5 h-5 text-brand-700" />
          </span>
          <div>
            <h2 className="text-xl md:text-2xl font-extrabold text-ink flex items-center gap-2">
              <Target className="w-6 h-6 text-brand-700 sm:hidden" aria-hidden="true" />
              Choose Your Path
            </h2>
            <p className="text-[15px] text-ink-muted mt-1">Start with the stage your child is at.</p>
          </div>
        </div>
        {/* Restyle: cards are now `h-full flex flex-col` with the coloured
            header set to `flex-1`. Previously each card sized to its own
            content, so a 2-line title or a 4th quick link left the row visibly
            ragged; now the header absorbs the difference and every card in the
            row starts and ends on the same line. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {educationGateways.map((gateway) => (
            <div
              key={gateway.id}
              className="h-full flex flex-col bg-canvas-raised rounded-2xl ring-1 ring-hairline shadow-sm overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
            >
              {/* Gateway Header
                  Restyle: these were four full-bleed saturated gradient panels,
                  which turned the most important section of the homepage into a
                  block of competing colour. Each door is now a white card
                  carrying its accent in two small places — a 3px top rule and
                  a tinted icon tile — so the four stay distinguishable at a
                  glance while the page reads as one calm system. It also fixes
                  a real legibility problem: body copy is now near-black on
                  white (~16:1) instead of white-on-mid-tone (~4:1). */}
              <span className={`h-[3px] w-full bg-gradient-to-r ${gateway.gradient}`} aria-hidden="true" />
              <button
                onClick={() => setPage(gateway.mainPage)}
                className="flex-1 w-full p-6 text-left group flex flex-col"
              >
                <span className={`flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br ${gateway.gradient} text-white shadow-sm mb-4 transition-transform duration-200 group-hover:scale-110`}>
                  <gateway.icon className="w-6 h-6" aria-hidden="true" />
                </span>
                <h3 className="text-lg font-extrabold leading-tight text-ink">{gateway.title}</h3>
                <p className="text-[13px] font-semibold text-ink-subtle mt-1">{gateway.subtitle}</p>
                <p className="text-sm text-ink-muted leading-relaxed mt-3">{gateway.description}</p>
                <span className="flex items-center gap-1 mt-4 pt-1 text-sm font-bold text-brand-700">
                  Explore
                  <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </button>
              {/* Gateway Resources */}
              <div className="p-4 bg-canvas-sunken border-t border-hairline">
                <p className="text-[11px] text-ink-subtle uppercase tracking-[0.1em] font-bold mb-2 px-2">Quick Links</p>
                <div className="space-y-0.5">
                  {gateway.resources.map((resource, idx) => (
                    <button
                      key={idx}
                      onClick={() => setPage(resource.page)}
                      className="group/link w-full flex items-center gap-2.5 px-2 min-h-[44px] rounded-lg hover:bg-canvas-raised text-left text-[15px] font-medium text-ink-muted hover:text-brand-700 transition-colors"
                    >
                      <resource.icon className="w-4 h-4 flex-shrink-0 text-ink-subtle group-hover/link:text-brand-600 transition-colors" aria-hidden="true" />
                      <span className="min-w-0 truncate">{resource.title}</span>
                      <ChevronRight className="w-4 h-4 ml-auto flex-shrink-0 opacity-0 -translate-x-1 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Inline Ad Slot 1 */}
      <InlineBanner1 page="home" />

      {/* For Every Family - cross-cutting resources, regardless of level */}
      <ForEveryFamilySection setPage={setPage} />

      {/* Support & Safety */}
      <SupportSafetySection setPage={setPage} />

      {/* Inline Ad Slot 2 */}
      <InlineBanner2 page="home" />

      {/* Service Providers Section */}
      <section className="bg-white/60 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/80">
        <h3 className="text-base md:text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
          <Store className="w-5 h-5 text-brand-600" />{t.serviceProviders}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button onClick={() => setPage('services')}
            className="group p-5 rounded-2xl bg-gradient-to-br from-brand-800 to-brand-900 text-white shadow-md hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                <Store className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <div className="text-left min-w-0">
                <span className="font-bold text-base md:text-lg block">{t.findServiceProviders}</span>
                <p className="text-brand-200 text-xs md:text-sm mt-1">{t.serviceProvidersDesc}</p>
              </div>
            </div>
          </button>
          <button onClick={() => setPage('getlisted')}
            className="group p-5 rounded-2xl bg-gradient-to-br from-amber-500 to-yellow-600 text-white shadow-md hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                <Send className="w-6 h-6 transition-transform duration-300 group-hover:scale-110" />
              </div>
              <div className="text-left min-w-0">
                <span className="font-bold text-base md:text-lg block">{t.getYourBusinessListed}</span>
                <p className="text-amber-100 text-xs md:text-sm mt-1">{t.getListedDesc}</p>
              </div>
            </div>
          </button>
        </div>
      </section>

      {/* Inline Ad Slot 3 */}
      <InlineBanner3 page="home" />

      {/* Bottom Banner Ad */}
      <BottomBanner page="home" />

      {/* Data Correction Report */}
      <DataCorrectionReport />
    </div>
  );
};

const SettingsPage = ({ setPage, goBack, lang, setLang }) => {
  const handleClear = () => { if (window.confirm('Clear all app data?')) { localStorage.clear(); window.location.reload(); }};

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3"><Settings className="w-8 h-8" />{T[lang].settings}</h2>
      </div>
      <div className="bg-white rounded-xl shadow-lg p-5">
        <h3 className="font-bold mb-4 flex items-center gap-2"><Globe className="w-5 h-5" />Language</h3>
        <div className="flex gap-3">
          <button onClick={() => setLang('en')} className={`flex-1 py-3 rounded-xl font-bold ${lang === 'en' ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700'}`}>English</button>
          <button onClick={() => setLang('patois')} className={`flex-1 py-3 rounded-xl font-bold ${lang === 'patois' ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700'}`}>Patois</button>
        </div>
      </div>
      <div className="bg-brand-50 border-2 border-brand-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <WifiOff className="w-6 h-6 text-brand-600" />
          <div><h4 className="font-bold text-brand-900">Offline Mode</h4><p className="text-brand-800 text-sm mt-1">This app works offline! All data stored locally.</p></div>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow-lg p-5">
        <h3 className="font-bold mb-4 flex items-center gap-2"><Trash2 className="w-5 h-5" />Data</h3>
        <button onClick={handleClear} className="w-full py-3 bg-red-100 hover:bg-red-200 text-red-700 font-bold rounded-xl">Clear All App Data</button>
      </div>
      <div className="bg-gray-50 rounded-xl p-5 text-center">
        <Shield className="w-12 h-12 text-brand-600 mx-auto mb-2" />
        <h4 className="font-bold">Jamaica Schools Guide</h4>
        <p className="text-sm text-gray-500">Version 2.1</p>
        <p className="text-xs text-gray-400 mt-1">Schools, Exams, Rankings & Education Resources</p>
      </div>
    </div>
  );
};

// ==================== DATA PROTECTION CONSENT MODAL ====================
const DataProtectionConsent = ({ onAccept }) => {
  const [checked, setChecked] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-brand-700 to-brand-800 text-white p-6 rounded-t-3xl">
          <div className="flex items-center gap-3 mb-2">
            <div className="relative">
              <GraduationCap className="w-12 h-12 text-yellow-400" />
              <BookOpen className="w-6 h-6 absolute -bottom-1 -right-1 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Jamaica Schools Guide</h2>
              <p className="text-brand-200 text-sm">Find Schools, Compare Outcomes, Plan Careers</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="font-bold text-blue-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Data Protection Notice
            </h3>
            <p className="text-blue-800 text-sm mt-2">
              In accordance with the <strong>Jamaica Data Protection Act, 2020</strong>, we inform you of how we handle your data.
            </p>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong>What we collect:</strong> Schools you save, documents you store, preferences, and search history. All data is stored locally on your device by default.
              </div>
            </div>

            <div className="flex gap-3">
              <Info className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong>Optional account:</strong> If you create an account, your data may be synced to our secure servers to enable features like service provider listings and cross-device access.
              </div>
            </div>

            <div className="flex gap-3">
              <Info className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong>Your rights:</strong> You have the right to access, correct, or delete your data at any time. You can delete your account and all associated data from Settings.
              </div>
            </div>

            <div className="flex gap-3">
              <Info className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" />
              <div>
                <strong>No selling:</strong> We do not sell your personal data to third parties.
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 mt-0.5"
              />
              <span className="text-sm text-gray-700">
                I have read and understand the data protection notice. I consent to the collection and processing of my data as described above and in the <a href="#" onClick={(e) => { e.preventDefault(); window.open('/privacy-policy', '_blank'); }} className="text-brand-600 underline hover:text-brand-700">Privacy Policy</a>.
              </span>
            </label>
          </div>

          <button
            onClick={onAccept}
            disabled={!checked}
            className={`w-full py-3 rounded-xl font-bold text-white transition-all ${
              checked
                ? 'bg-brand-600 hover:bg-brand-700 shadow-lg hover:shadow-xl'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            Continue to App
          </button>

          <p className="text-xs text-gray-500 text-center">
            By continuing, you agree to our Terms of Use and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== PRIVACY POLICY PAGE ====================
const PrivacyPolicyPage = ({ setPage, goBack }) => {
  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Shield className="w-8 h-8" />
          Privacy Policy
        </h2>
        <p className="text-slate-300 mt-1">Jamaica Schools Guide - Data Protection</p>
      </div>

      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 space-y-6">
        <div>
          <p className="text-sm text-gray-500">Last Updated: 11 August 2026</p>
          <p className="mt-3 text-gray-700">
            Jamaica Schools Guide ("we", "our", "us") is committed to protecting your privacy in accordance with the
            <strong> Jamaica Data Protection Act, 2020</strong>. This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you use our application.
          </p>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-brand-600" />
            1. Information We Collect
          </h3>
          <div className="pl-7 space-y-2 text-gray-700">
            <p><strong>Local Data (stored on your device only):</strong></p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Schools you save to favourites</li>
              <li>Search preferences and filters</li>
              <li>App preferences and settings</li>
              <li>Education documents you store</li>
            </ul>
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                <strong>Important:</strong> This data is stored only in your browser's local storage and is NOT transmitted to any server.
                Clearing your browser data, cache, or using private/incognito mode will permanently delete this information.
              </p>
            </div>
            <p className="mt-3"><strong>Account Data (if you register):</strong></p>
            <ul className="list-disc pl-5 space-y-1 text-sm">
              <li>Email address</li>
              <li>Name (optional)</li>
              <li>Phone number (optional, for service providers)</li>
              <li>Business information (for service providers)</li>
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Info className="w-5 h-5 text-brand-600" />
            2. How We Use Your Information
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>We use your information to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Provide app functionality (school search, saved schools, comparisons)</li>
              <li>Enable account features and cross-device synchronization</li>
              <li>Display education service provider listings to users</li>
              <li>Improve our services and user experience</li>
              <li>Comply with legal obligations under Jamaican law</li>
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Lock className="w-5 h-5 text-brand-600" />
            3. Data Security
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>
              We implement appropriate technical and organizational measures to protect your personal data against
              unauthorized access, alteration, disclosure, or destruction. This includes:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Encrypted data transmission (HTTPS/TLS)</li>
              <li>Secure database storage with access controls</li>
              <li>Local-first data storage when possible</li>
              <li>Regular security assessments</li>
            </ul>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-brand-600" />
            4. Your Rights Under the Jamaica Data Protection Act
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>Under the Jamaica Data Protection Act, 2020, you have the right to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Object:</strong> Object to processing of your data for marketing</li>
              <li><strong>Withdraw Consent:</strong> Withdraw your consent at any time</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, go to Settings in the app or contact us at the address below.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-brand-600" />
            5. Data Retention
          </h3>
          <div className="pl-7 text-gray-700 text-sm">
            <p>
              We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected,
              or as required by law. Local data remains on your device until you delete it. Account data is retained until
              you delete your account.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Globe className="w-5 h-5 text-brand-600" />
            6. Third-Party Services
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>Our app may use the following third-party services:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Supabase:</strong> For account authentication and data storage</li>
              <li><strong>Leaflet/OpenStreetMap:</strong> For map functionality</li>
              <li><strong>Google Fonts:</strong> For typography</li>
              <li><strong>Google Analytics:</strong> For anonymous, aggregated usage analytics that help us improve the site</li>
              <li><strong>Google AdSense:</strong> To display advertising that helps keep this guide free</li>
            </ul>
            <p className="mt-2">
              These services have their own privacy policies. We do not sell your personal data to any third party.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-brand-600" />
            6a. Advertising &amp; Cookies
          </h3>
          <div className="pl-7 text-gray-700 text-sm space-y-2">
            <p>
              We use cookies and similar technologies to run the site, remember your preferences, measure traffic,
              and — where enabled — serve advertising.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Third-party vendors, including <strong>Google</strong>, use cookies to serve ads based on a user's prior
                visits to this and other websites.
              </li>
              <li>
                Google's use of advertising cookies enables it and its partners to serve ads to our users based on
                their visit to this site and/or other sites on the Internet.
              </li>
              <li>
                You may opt out of personalised advertising by visiting{' '}
                <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-brand-600 underline hover:text-brand-700">Google Ads Settings</a>,
                or opt out of some third-party vendors' use of cookies at{' '}
                <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-brand-600 underline hover:text-brand-700">aboutads.info/choices</a>.
              </li>
              <li>
                You can also control or delete cookies through your browser settings at any time.
              </li>
            </ul>
            <p>
              Advertising is kept separate from our educational content: <strong>ads never affect school rankings,
              exam data, or any tools on this site.</strong>
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-brand-600" />
            7. Children's Privacy
          </h3>
          <div className="pl-7 text-gray-700 text-sm">
            <p>
              This app is not intended for children under 16. We do not knowingly collect personal data from children.
              If you believe we have collected data from a child, please contact us immediately.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-brand-600" />
            8. Changes to This Policy
          </h3>
          <div className="pl-7 text-gray-700 text-sm">
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new
              Privacy Policy on this page and updating the "Last Updated" date.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-brand-600" />
            9. Contact Us
          </h3>
          <div className="pl-7 text-gray-700 text-sm">
            <p>
              If you have questions about this Privacy Policy or wish to exercise your data protection rights,
              please contact us:
            </p>
            <div className="mt-4">
              <button
                onClick={() => setPage('contact')}
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
              >
                <Send className="w-4 h-4" />
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== CONTACT US PAGE ====================
const ContactUsPage = ({ setPage, goBack }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    category: 'general',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const categories = [
    { id: 'general', label: 'General Inquiry' },
    { id: 'feedback', label: 'Feedback & Suggestions' },
    { id: 'bug', label: 'Report a Bug' },
    { id: 'business', label: 'Business Listing Inquiry' },
    { id: 'data', label: 'Data Protection Request' },
    { id: 'partnership', label: 'Partnership Opportunity' },
    { id: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // Import dynamically to avoid circular deps
      const { contactSubmissions } = await import('./lib/supabase');
      const { error: submitError } = await contactSubmissions.submit(form);

      if (submitError) throw submitError;
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="space-y-6">
        <BackButton setPage={setPage} goBack={goBack} />
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-8 text-center">
          <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-brand-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for contacting us. We'll review your message and get back to you as soon as possible.
          </p>
          <button
            onClick={() => setPage('home')}
            className="px-6 py-3 bg-brand-600 text-white font-bold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton setPage={setPage} goBack={goBack} />
      <div className="bg-gradient-to-r from-brand-600 to-brand-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Send className="w-8 h-8" />
          Contact Us
        </h2>
        <p className="text-blue-200 mt-1">We'd love to hear from you</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Contact Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6 space-y-5">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {error}
              </div>
            )}

            {/* Name & Email Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* Phone & Category Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="876-xxx-xxxx"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Brief description of your inquiry"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Please provide as much detail as possible..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-4 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${
                submitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl'
              }`}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Send Message
                </>
              )}
            </button>
          </form>
        </div>

        {/* Contact Info Sidebar */}
        <div className="space-y-4">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/60 p-6">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              Get in Touch
            </h3>
            <div className="space-y-4 text-sm text-gray-600">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-800">Response Time</p>
                  <p>We typically respond within 24-48 hours</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-800">Location</p>
                  <p>Kingston, Jamaica</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
            <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Data Protection
            </h4>
            <p className="text-blue-800 text-sm">
              For data protection requests (access, correction, deletion), please select "Data Protection Request" as the category and include your registered email.
            </p>
          </div>

          <div className="bg-brand-50 border border-brand-200 rounded-2xl p-5">
            <h4 className="font-bold text-brand-900 mb-2 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Business Inquiries
            </h4>
            <p className="text-brand-800 text-sm">
              Interested in listing your business or partnership opportunities? We'd love to hear from you!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== URL ROUTING UTILITIES ====================
// Valid pages for URL routing - must match renderPage() switch cases
const VALID_PAGES = new Set([
  // Core Education Pages
  'home', 'schools', 'how-school-works', 'enrollment', 'tertiary', 'primary', 'second-chance',
  'exams', 'exams-pep', 'exams-csec', 'exams-cape', 'exams-nvqj', 'exams-cityguilds', 'exams-sat',
  'career-path', 'life-path', 'special-education', 'child-protection',
  'rankings', 'rankings-academic', 'rankings-extracurricular', 'rankings-environment', 'rankings-popularity',
  'share-sentiment', 'suggest-correction', 'submit-achievement',
  'funding', 'education-jobs', 'help', 'how-guide-works',
  'compare-schools', 'schools-by-parish', 'all-schools', 'exam-tracker',
  'side-hustles', 'summer-employment', 'opportunity-matcher', 'extracurriculars',
  // Tools & Services
  'askbot', 'services', 'getlisted',
  // Utility pages
  'settings', 'privacy-policy', 'contact', 'about', 'terms', 'updates',
  // Auth & User pages
  'login', 'dashboard', 'my-analytics', 'my-business', 'vendor-portal',
  // Admin pages
  'admin', 'admin-submissions', 'admin-users', 'admin-listings',
  'admin-analytics', 'admin-ads', 'admin-hero', 'admin-corrections', 'admin-notifications',
  'admin-contact', 'admin-settings', 'admin-service-providers', 'admin-subscriptions',
  'admin-payments', 'admin-email-templates', 'admin-payment-requests', 'admin-provider-reports', 'admin-provider-reviews',
  'admin-schools', 'admin-exam-results', 'admin-disruptions', 'admin-suggestions',
  'admin-student-engagement', 'admin-reviews', 'admin-jobs'
]);

// Pages on which the AdSense loader (and therefore Auto ads) is allowed to run.
// Explicit allowlist (not a blocklist) so ads only appear on content-rich,
// brand-safe pages and NEVER on:
//  - auth/account/admin screens or behind logins (AdSense policy: no ads on
//    screens without publisher content or behind a login)
//  - forms and thank-you screens (getlisted, contact, suggest-correction, …)
//  - sensitive content (child-protection) where ads are inappropriate
// Thin utility/submission pages kept out of the search index (mirrors the
// `noindex: true` flags in scripts/seo-routes.js used by the prerender/sitemap).
const NOINDEX_PAGES = new Set(['suggest-correction', 'submit-achievement']);

const AD_ELIGIBLE_PAGES = new Set([
  'home', 'schools', 'how-school-works', 'enrollment', 'tertiary', 'primary', 'second-chance',
  'exams', 'exams-pep', 'exams-csec', 'exams-cape', 'exams-nvqj', 'exams-cityguilds', 'exams-sat',
  'career-path', 'life-path', 'special-education',
  'rankings', 'rankings-academic', 'rankings-extracurricular', 'rankings-environment', 'rankings-popularity',
  'funding', 'education-jobs', 'help', 'how-guide-works',
  'updates', 'about',
  'compare-schools', 'schools-by-parish',
  'side-hustles', 'summer-employment', 'opportunity-matcher', 'extracurriculars',
  'services'
]);

// Get page from URL - supports both clean paths (/schools) and hash (#schools) for compatibility
const getPageFromUrl = () => {
  // First check hash (for backwards compatibility and direct hash links)
  const hash = window.location.hash.slice(1);
  if (hash) {
    const hashPage = hash.split('?')[0];
    if (VALID_PAGES.has(hashPage)) return hashPage;
    // Handle dynamic school-{id} routes
    if (hashPage.startsWith('school-')) return hashPage;
  }

  // Then check pathname for clean URLs
  const pathname = window.location.pathname.slice(1); // Remove leading /
  if (!pathname || pathname === '') return 'home';
  const pathPage = pathname.split('/')[0].split('?')[0];
  // Handle dynamic school-{id} routes
  if (pathPage.startsWith('school-')) return pathPage;
  return VALID_PAGES.has(pathPage) ? pathPage : 'home';
};

// ==================== MAIN APP CONTENT ====================
function AppContent() {
  // Initialize page from URL for SEO and direct linking
  const [page, setPage] = useState(() => getPageFromUrl());
  const [menuOpen, setMenuOpen] = useState(false);
  const [lang, setLang] = useLocalStorage('ja-lang', 'en');
  const [online, setOnline] = useState(navigator.onLine);
  const [selectedOffenceCode, setSelectedOffenceCode] = useState(null); // For deep-linking from chatbot
  const [selectedVendor, setSelectedVendor] = useState(null); // For My Business direct access
  const [hasConsented, setHasConsented] = useLocalStorage('ja-dpa-consent', false); // Data Protection Act consent
  const { isAdmin } = useAuth();

  // Track scroll positions and navigation history for back button
  const scrollPositions = useRef({});
  const pageHistory = useRef(['home']);
  const isGoingBack = useRef(false);
  const isNavigating = useRef(false);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const newPage = getPageFromUrl();
      if (newPage !== page) {
        isGoingBack.current = true;
        setPage(newPage);
        // Update history tracking
        if (pageHistory.current[pageHistory.current.length - 1] !== newPage) {
          pageHistory.current.push(newPage);
        }
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [page]);

  // Handle URL parameters on initial load (for vendor portal, etc.)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    if (pageParam === 'vendor-portal' && urlParams.get('token')) {
      setPage('vendor-portal');
    }
  }, []);

  // Handle scroll position on page changes
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (isGoingBack.current) {
        // Restore scroll position when going back
        const savedPosition = scrollPositions.current[page] || 0;
        window.scrollTo({ top: savedPosition, behavior: 'instant' });
        isGoingBack.current = false;
      } else {
        // Scroll to top for new pages
        window.scrollTo({ top: 0, behavior: 'instant' });
      }
      // Navigation complete
      isNavigating.current = false;
    });
  }, [page]);

  // Initialize GA4 on app mount (if configured and enabled)
  useEffect(() => {
    // Analytics is optional processing and must not begin behind the consent
    // notice. This also prevents the first page view from being sent before a
    // visitor has made a choice.
    if (!hasConsented) return;

    const initAnalytics = async () => {
      try {
        // 1) Measurement ID can come from an environment variable (set in the
        //    hosting platform, like the Supabase/Maps keys) OR from admin settings.
        //    Env var takes effect even if Supabase isn't reachable.
        const envMeasurementId = String(
          process.env.REACT_APP_GA4_MEASUREMENT_ID ||
          process.env.REACT_APP_GA_MEASUREMENT_ID ||
          ''
        ).trim();

        // 2) Admin-panel settings (stored in Supabase app_settings)
        const [measurementIdResult, enabledResult] = await Promise.all([
          appSettings.get('ga4_measurement_id'),
          appSettings.get('ga4_enabled')
        ]);

        // Strip quotes/whitespace (values may be stored as JSON strings)
        const settingsMeasurementId = String(measurementIdResult?.data || '').replace(/^"|"$/g, '').trim();
        const enabledFlag = String(enabledResult?.data ?? '').replace(/^"|"$/g, '').trim().toLowerCase();

        // Env var wins for the ID; otherwise use the saved setting
        const measurementId = envMeasurementId || settingsMeasurementId;

        // Enablement:
        //  - 'true'  => on
        //  - 'false' => explicitly off (respect the admin toggle)
        //  - unset   => on whenever a measurement ID is configured (env var or a
        //               saved ID counts as intent to track — fixes the common case
        //               where the ID was added but the toggle was never flipped)
        let isEnabled;
        if (enabledFlag === 'true') isEnabled = true;
        else if (enabledFlag === 'false') isEnabled = false;
        else isEnabled = Boolean(measurementId);

        console.log('GA4 Config:', {
          isEnabled,
          measurementId,
          source: envMeasurementId ? 'env' : (settingsMeasurementId ? 'settings' : 'none'),
          rawEnabled: enabledResult?.data
        });

        if (!measurementId) {
          console.log('GA4 not configured (no measurement ID in env vars or admin settings)');
          return;
        }

        if (!isEnabled) {
          console.log('GA4 tracking is disabled (admin toggle is off)');
          return;
        }

        if (!measurementId.startsWith('G-')) {
          console.warn(`GA4 measurement ID "${measurementId}" is invalid — it must start with "G-" (e.g. G-XXXXXXXXXX)`);
          return;
        }

        const initialized = await initGA4(measurementId);
        if (initialized) {
          console.log('GA4 successfully initialized');
          const seoConfig = PAGE_SEO[page] || PAGE_SEO.home;
          trackPageView(page, seoConfig?.title || page);
        }
      } catch (err) {
        console.warn('GA4 initialization skipped:', err);
      }
    };
    initAnalytics();
  }, [hasConsented]);

  // Load Google AdSense (and therefore Auto ads) ONLY on brand-safe content
  // pages. The loader is no longer a static tag in index.html, so on a direct
  // landing on an excluded route (child-protection, admin, login, forms, …) the
  // script never loads and Auto ads never runs there. NOTE: this is an SPA — once
  // the script has loaded on an eligible page it persists for the session, so
  // pages reached by in-app navigation afterwards can still be eligible for Auto
  // ads. Pair this with AdSense → Auto ads → Manage URL exclusions for airtight
  // control of admin/login/child-protection.
  useEffect(() => {
    if (isAdSenseConfigured() && AD_ELIGIBLE_PAGES.has(page)) {
      initAdSense();
    }
  }, [page]);

  // Track page views when page changes
  useEffect(() => {
    if (page) {
      const seoConfig = PAGE_SEO[page] || PAGE_SEO.home;
      const pageTitle = seoConfig?.title || page;
      trackPageView(page, pageTitle);
    }
  }, [page]);

  // Enhanced setPage that tracks history, scroll positions, and updates URL
  // `query` lets a link carry state the destination can read, e.g.
  // navigateTo('all-schools', { level: 'Primary' }) -> /all-schools?level=Primary.
  // Deliberately in the URL rather than in React state: the filtered directory
  // is then shareable, bookmarkable and survives a refresh, which page-level
  // state would not.
  const navigateTo = (newPage, query) => {
    const search = query ? `?${new URLSearchParams(query).toString()}` : '';
    // Re-navigating to the same page is normally a no-op, but not when the
    // query differs — that is a real change of view.
    const sameTarget = newPage === page && search === window.location.search;
    if (isNavigating.current || sameTarget) return;
    isNavigating.current = true;
    // Save current scroll position before leaving
    scrollPositions.current[page] = window.scrollY;
    // Add to history
    pageHistory.current.push(newPage);
    // Update URL with clean path for SEO (e.g., /offences instead of /#offences)
    const newPath = (newPage === 'home' ? '/' : `/${newPage}`) + search;
    window.history.pushState({ page: newPage }, '', newPath);
    setPage(newPage);
  };

  // Go back to previous page with scroll restoration
  const goBack = () => {
    // Prevent rapid navigation issues
    if (isNavigating.current) return;

    // Save current scroll position before going back
    scrollPositions.current[page] = window.scrollY;
    isGoingBack.current = true;

    // Use browser's back to trigger popstate and stay in sync with browser history
    if (window.history.length > 1) {
      window.history.back();
    } else {
      // Fallback to home if no browser history
      navigateTo('home');
    }
  };

  // Helper to navigate to offences with a specific offence selected
  const navigateToOffence = (offenceCode, category) => {
    setSelectedOffenceCode({ code: offenceCode, category });
    navigateTo('offences');
  };

  const renderPage = () => {
    // Use navigateTo for setPage to track history, and provide goBack for back buttons
    const props = { lang, setLang, setPage: navigateTo, goBack };
    // Some cases below (admin, login, vendor) pass setPage directly without {...props}.
    // Alias setPage to navigateTo here so their navigation is history-tracked like every
    // other page — otherwise the browser Back button jumps straight out of the admin area
    // (e.g. back to the login screen) because admin sub-navigation added no history entries.
    const setPage = navigateTo;
    const seoConfig = PAGE_SEO[page] || PAGE_SEO.home;
    // Keep thin submission-form pages out of search indexes (mirrors the
    // noindex flags in scripts/seo-routes.js so runtime + prerender agree).
    const seoNoindex = NOINDEX_PAGES.has(page);

    return (
      <>
        <SEO page={page} title={seoConfig.title} description={seoConfig.description} noindex={seoNoindex} />
        {(() => {
          switch(page) {
            case 'home': return <HomePage setPage={setPage} {...props} />;
            // Education Pages
            case 'schools': return <SecondaryEducationHub setPage={setPage} {...props} />;
            case 'all-schools': return <AllSchoolsPage setPage={setPage} {...props} />;
            case 'how-school-works': return <HowSchoolWorksPage setPage={setPage} {...props} />;
            case 'enrollment': return <EnrollmentPage setPage={setPage} {...props} />;
            case 'exams': return <ExamsHubPage setPage={setPage} {...props} />;
            case 'exams-pep': return <PEPExamPage setPage={setPage} {...props} />;
            case 'exams-csec': return <CSECExamPage setPage={setPage} {...props} />;
            case 'exams-cape': return <CAPEExamPage setPage={setPage} {...props} />;
            case 'exams-nvqj': return <NVQJExamPage setPage={setPage} {...props} />;
            case 'exams-cityguilds': return <CityGuildsExamPage setPage={setPage} {...props} />;
            case 'exams-sat': return <SATExamPage setPage={setPage} {...props} />;
            case 'career-path': return <CareerPathVisualiser setPage={setPage} {...props} />;
            case 'life-path': return <LifePathSimulator setPage={setPage} {...props} />;
            case 'special-education': return <SpecialEducationSupportPage setPage={setPage} {...props} />;
            case 'child-protection': return <ChildProtectionPage setPage={setPage} {...props} />;
            case 'rankings': return <RankingsHubPage setPage={setPage} {...props} />;
            case 'rankings-academic': return <AcademicRankingsPage setPage={setPage} {...props} />;
            case 'rankings-extracurricular': return <ExtracurricularRankingsPage setPage={setPage} {...props} />;
            case 'rankings-environment': return <EnvironmentRankingsPage setPage={setPage} {...props} />;
            case 'rankings-popularity': return <HotSchoolsPage setPage={setPage} {...props} />;
            case 'share-sentiment': return <ShareSentimentPage setPage={setPage} {...props} />;
            case 'suggest-correction': return <SuggestCorrectionPage setPage={setPage} {...props} />;
            case 'submit-achievement': return <SubmitAchievementPage setPage={setPage} {...props} />;
            case 'funding': return <FundingPage setPage={setPage} {...props} />;
            case 'education-jobs': return <EducationJobsPage setPage={setPage} {...props} />;
            case 'help': return <HelpPage setPage={setPage} {...props} />;
            case 'how-guide-works': return <HowThisGuideWorksPage setPage={setPage} {...props} />;
            case 'compare-schools': return <CompareSchoolsPage setPage={setPage} {...props} />;
            case 'schools-by-parish': return <SchoolsByParishPage setPage={setPage} {...props} />;
            case 'exam-tracker': return <ExamResourcesPage setPage={setPage} {...props} />;
            case 'side-hustles': return <StudentSideHustlesPage setPage={setPage} {...props} />;
            case 'summer-employment': return <SummerEmploymentPage setPage={setPage} {...props} />;
            case 'opportunity-matcher': return <OpportunityMatcherPage setPage={setPage} {...props} />;
            case 'extracurriculars': return <ExtracurricularsPage setPage={setPage} {...props} />;
            case 'tertiary': return <TertiaryEducationHub setPage={setPage} {...props} />;
            case 'primary': return <PrimaryEducationHub setPage={setPage} {...props} />;
            case 'second-chance': return <SecondChanceEducationHub setPage={setPage} {...props} />;
            case 'tertiary-polls': return <TertiaryPollsPage setPage={setPage} {...props} />;
            case 'primary-polls': return <PrimaryPollsPage setPage={setPage} {...props} />;
            case 'askbot': return <EducationAskBot setPage={setPage} {...props} />;
            // Service Providers - consolidated
            case 'services': return <ServiceProvidersPage setPage={setPage} {...props} />;
            case 'getlisted': return <GetListedPageEnhanced setPage={setPage} {...props} />;
            case 'settings': return <SettingsPage {...props} />;
            case 'privacy-policy': return <PrivacyPolicyPage {...props} />;
            case 'contact': return <ContactUsPage {...props} />;
            case 'about': return <AboutEditorialPage {...props} />;
            case 'terms': return <TermsPage {...props} />;
            case 'updates': return <EducationUpdatesPage {...props} />;
            // Auth & User Pages
            case 'login': return <LoginPage setPage={setPage} />;
            case 'dashboard': return <AuthGate setPage={setPage} pageName="My Dashboard" pageIcon={User}><UserDashboard setPage={setPage} {...props} /></AuthGate>;
            case 'my-analytics': return <AdvertiserPortal setPage={setPage} />;
            // Admin Pages
            case 'admin': return <AdminDashboard setPage={setPage} />;
            case 'admin-submissions': return <AdminSubmissionsPageEnhanced setPage={setPage} />;
            case 'admin-users': return <AdminUsersPage setPage={setPage} />;
            case 'admin-listings': return <AdminAllListingsPage setPage={setPage} />;
            case 'admin-analytics': return <AdminAnalyticsPage setPage={setPage} />;
            case 'admin-ads': return <AdminBannerAdsPage setPage={setPage} />;
            case 'admin-hero': return <AdminHeroCarouselPage setPage={setPage} />;
            case 'admin-corrections': return <AdminDataCorrectionsPage setPage={setPage} />;
            case 'admin-notifications': return <AdminNotificationsPage setPage={setPage} />;
            case 'admin-contact': return <AdminContactSubmissionsPage setPage={setPage} />;
            case 'admin-settings': return <AdminSettingsPageEnhanced setPage={setPage} />;
            // Service Provider Management
            case 'admin-service-providers': return <AdminServiceProvidersPage setPage={setPage} />;
            case 'admin-subscriptions': return <AdminSubscriptionsPage setPage={setPage} />;
            case 'admin-payments': return <AdminPaymentsPage setPage={setPage} />;
            case 'admin-email-templates': return <AdminEmailTemplatesPage setPage={setPage} />;
            case 'admin-payment-requests': return <AdminPaymentRequestsPage setPage={setPage} />;
            case 'admin-provider-reports': return <AdminProviderReportsPage setPage={setPage} />;
            case 'admin-provider-reviews': return <AdminProviderReviewsPage setPage={setPage} />;
            // School Data Management
            case 'admin-schools': return <AdminSchoolsPage setPage={setPage} />;
            case 'admin-exam-results': return <AdminExamResultsPage setPage={setPage} />;
            case 'admin-disruptions': return <AdminDisruptionsPage setPage={setPage} />;
            case 'admin-suggestions': return <AdminSuggestionsPage setPage={setPage} />;
            case 'admin-student-engagement': return <AdminStudentEngagementPage setPage={setPage} />;
            case 'admin-reviews': return <AdminSchoolReviewsPage setPage={setPage} />;
            case 'admin-jobs': return <AdminEducationJobsPage setPage={setPage} />;
            // Vendor Portal (token-based access)
            case 'vendor-portal': {
              const urlParams = new URLSearchParams(window.location.search);
              const token = urlParams.get('token');
              return <VendorPortalPage setPage={setPage} token={token} />;
            }
            // My Business Portal (direct access for logged-in owners)
            case 'my-business': {
              if (!selectedVendor) {
                return <HomePage setPage={setPage} {...props} />;
              }
              return <VendorPortalPage
                setPage={setPage}
                vendorId={selectedVendor.id}
                providerTable={selectedVendor.providerTable}
              />;
            }
            default: {
              // Handle dynamic school profile routes (school-{id})
              if (page.startsWith('school-')) {
                const schoolId = page.replace('school-', '');
                return <SchoolProfilePage schoolId={schoolId} setPage={setPage} {...props} />;
              }
              return <HomePage setPage={setPage} {...props} />;
            }
          }
        })()}
      </>
    );
  };

  return (
    // Restyle: was `bg-gradient-to-br from-slate-200 via-slate-100 to-blue-50`
    // — a muddy cool gradient that made every white card look like it was
    // floating on a bruise. Now a flat warm near-white canvas (see the
    // `canvas` token) so content, not the backdrop, carries the colour.
    <div className="min-h-screen bg-canvas flex flex-col">
      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[200] focus:bg-brand-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:font-semibold focus:shadow-lg">
        Skip to main content
      </a>
      {/* The Plus Jakarta Sans @import that used to live here has moved to a
          real <link> in public/index.html, and the blanket `* { font-family }`
          rule is now the `sans` token in tailwind.config.js. Injecting it from
          React meant the font could not begin downloading until the bundle had
          parsed, so every cold load flashed Helvetica first. */}

      {/* Data Protection Consent Modal - shows on first visit */}
      {!hasConsented && (
        <DataProtectionConsent onAccept={() => setHasConsented(true)} />
      )}

      {!online && (
        <div className="bg-amber-500 text-white text-center py-2 text-sm font-medium flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />{T[lang]?.youreOffline || T.en.youreOffline}
        </div>
      )}
      <Header
        page={page}
        setPage={navigateTo}
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        lang={lang}
        setLang={setLang}
        setSelectedVendor={setSelectedVendor}
      />
      <main id="main-content" role="main" className="flex-1 w-full max-w-7xl mx-auto px-4 py-6 sm:py-8 md:py-10">{renderPage()}</main>

      {/* Restyle: the footer was a single centred row of pipe-separated links
          on bg-gray-900, with blue-400 link colour that matched nothing else on
          the site. It is now a three-column structure on the brand's darkest
          shade, with grouped headings — which also gives the secondary pages
          (Terms, About, Contact) a real home instead of a run-on list. */}
      <footer role="contentinfo" className="bg-brand-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            <div>
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 ring-1 ring-white/20">
                  <GraduationCap className="w-6 h-6 text-white" aria-hidden="true" />
                </span>
                <div>
                  <div className="font-extrabold">Jamaica Schools Guide</div>
                  <div className="text-sm text-brand-200">Find Schools, Compare Outcomes</div>
                </div>
              </div>
              <p className="mt-4 text-sm text-brand-200 leading-relaxed max-w-xs">
                Helping Jamaican families make informed education decisions, from
                basic school through university.
              </p>
            </div>

            <FooterNav
              title="Explore"
              navigateTo={navigateTo}
              links={[
                { id: 'primary', label: 'Prep & Primary' },
                { id: 'schools', label: 'High Schools' },
                { id: 'tertiary', label: 'Universities & Colleges' },
                { id: 'second-chance', label: 'Second Chance' },
                { id: 'rankings', label: 'Rankings' },
                { id: 'funding', label: 'Scholarships' },
              ]}
            />

            <FooterNav
              title="About & Policies"
              navigateTo={navigateTo}
              links={[
                { id: 'about', label: 'About & Editorial' },
                { id: 'how-guide-works', label: 'How This Guide Works' },
                { id: 'updates', label: 'News & Updates' },
                { id: 'contact', label: 'Contact Us' },
                { id: 'privacy-policy', label: 'Privacy Policy' },
                { id: 'terms', label: 'Terms' },
              ]}
            />
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-brand-300">
            <p>© {new Date().getFullYear()} Jamaica Schools Guide. Developed by Johnathon Kelly.</p>
            <p>Version 2.1</p>
          </div>
        </div>
      </footer>
      <BackToTop />
    </div>
  );
}

// ==================== APP WITH AUTH PROVIDER ====================
export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <WebsiteSchema />
        <OrganizationSchema />
        <AppContent />
      </AuthProvider>
    </HelmetProvider>
  );
}
